import React, { useState, useEffect } from "react";
import NewCommonModalCenter from "../../../components/common/NewCommonModalCenter";
import toast from "react-hot-toast";
import {
  IconCheck,
  IconUser,
  IconBriefcase,
  IconEye,
  IconChevronLeft,
  IconChevronRight,
  IconX,
  IconChevronDown,
} from "@tabler/icons-react";
import {
  fetchHrCompany,
  fetchHrBranches,
  fetchHrDepartments,
  fetchHrDesignations,
  fetchHrEmployees,
  fetchHrCountries,
  fetchHrCities,
  fetchHrDistricts,
  createHrEmployee,
} from "../../../apis/hrApi";
import { useRolesData } from "../../../apis/staffApis";

const SectionBlock = ({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
}) => (
  <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-slate-200/50 shadow-sm p-4 transition-all duration-300 space-y-4 relative">
    <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/10 to-transparent pointer-events-none" />
    <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3 relative z-10">
      <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
        <Icon size={16} className="text-[#212121]" stroke={1.5} />
      </div>
      <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
        {title}
      </p>
    </div>
    <div className="relative z-10">
      {children}
    </div>
  </div>
);

interface InputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
}

const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>(
  ({ label, placeholder, type = "text", className = "", error, required, ...rest }, ref) => {
    return (
      <div className={`w-full space-y-1 ${className}`}>
        <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <input
            ref={ref}
            type={type}
            placeholder={placeholder}
            {...rest}
            className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
          />
        </div>
        {error && <p className="text-red-500 text-xs">{error}</p>}
      </div>
    );
  }
);
InputField.displayName = "InputField";

const SelectInput = ({
  label,
  value,
  setValue,
  selectOptions = [],
  className = "",
  required,
  disabled,
}: {
  label: string;
  value?: string | number;
  setValue?: (val: string) => void;
  selectOptions?: { value: string | number; text: string }[] | false;
  className?: string;
  required?: boolean;
  disabled?: boolean;
}) => {
  return (
    <div className={`space-y-1 w-full ${className}`}>
      <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative flex items-center w-full">
        <select
          value={value}
          onChange={(e) => setValue && setValue(e.target.value)}
          disabled={disabled}
          className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed cursor-pointer"
        >
          <option value="">Select</option>
          {selectOptions &&
            selectOptions.map((opt, i) => (
              <option key={i} value={opt.value}>
                {opt.text}
              </option>
            ))}
        </select>
      </div>
    </div>
  );
};

interface TextareaFieldProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

const TextareaField = ({
  label,
  name,
  placeholder,
  rows = 3,
  value,
  onChange,
  disabled,
  ...rest
}: TextareaFieldProps) => {
  return (
    <div className="w-full space-y-1">
      {label && (
        <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">
          {label}
        </label>
      )}
      <textarea
        name={name}
        placeholder={placeholder}
        rows={rows}
        value={value}
        onChange={onChange}
        disabled={disabled}
        {...rest}
        className="w-full rounded-xl py-2 px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
      />
    </div>
  );
};

type AddEmployeeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

export default function AddEmployeeModal({
  isOpen,
  onClose,
  onSuccess,
}: AddEmployeeModalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyId, setCompanyId] = useState("");

  // Masters options
  const [branches, setBranches] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [designations, setDesignations] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);
  const [managers, setManagers] = useState<any[]>([]);

  // Roles from react-query hook
  const { data: rolesData } = useRolesData();
  const roles = rolesData?.roles || [];

  // Form state reflecting backend schema keys
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    branchId: "",
    departmentId: "",
    designationId: "",
    personalEmail: "",
    workEmail: "",
    phoneNumber: "",
    gender: "",
    maritalStatus: "",
    bloodGroup: "",
    nationality: "",
    dateOfBirth: "",
    joiningDate: "",
    employmentType: "",
    attendanceType: "",
    salaryCycle: "MONTHLY",
    annualCtc: "",
    yearsOfExperience: "",
    reportingToId: "",
    userRoleId: "",
    currentWork: "",
    pastWork: "",
    previousCompany: "",
    iqamaNumber: "",
    iqamaDoc: "",
    shortAddress: "",
    currentAddress: "",
    permanentAddress: "",
    sameAsPermanentAddress: false,
    countryId: "",
    cityId: "",
    districtId: "",
    zipCode: "",
    status: "ACTIVE",
  });

  // Load initial master data
  useEffect(() => {
    if (!isOpen) return;

    // Reset Form and Steps
    setCurrentStep(1);
    setFormData({
      firstName: "",
      lastName: "",
      branchId: "",
      departmentId: "",
      designationId: "",
      personalEmail: "",
      workEmail: "",
      phoneNumber: "",
      gender: "",
      maritalStatus: "",
      bloodGroup: "",
      nationality: "Saudi Arabia",
      dateOfBirth: "",
      joiningDate: "",
      employmentType: "",
      attendanceType: "",
      salaryCycle: "MONTHLY",
      annualCtc: "",
      yearsOfExperience: "",
      reportingToId: "",
      userRoleId: "",
      currentWork: "",
      pastWork: "",
      previousCompany: "",
      iqamaNumber: "",
      iqamaDoc: "",
      shortAddress: "",
      currentAddress: "",
      permanentAddress: "",
      sameAsPermanentAddress: false,
      countryId: "",
      cityId: "",
      districtId: "",
      zipCode: "",
      status: "ACTIVE",
    });

    const loadInitialData = async () => {
      try {
        // Fetch Company details for companyId
        const compRes = await fetchHrCompany();
        if (compRes.success && compRes.data) {
          setCompanyId(compRes.data.id || compRes.data._id || "");
        }

        // Fetch Branches
        const branchRes = await fetchHrBranches(1, 100);
        if (branchRes.success && branchRes.data?.branches) {
          setBranches(branchRes.data.branches);
        }

        // Fetch Countries
        const countryRes = await fetchHrCountries();
        if (countryRes.success && countryRes.data?.countries) {
          setCountries(countryRes.data.countries);
        }

        // Fetch Employees for manager dropdown
        const empRes = await fetchHrEmployees(1, 1000);
        if (empRes.success && empRes.data?.employees) {
          setManagers(empRes.data.employees);
        }
      } catch (err) {
        console.error("Error loading master data:", err);
        toast.error("Failed to load organizational master data");
      }
    };

    loadInitialData();
  }, [isOpen]);

  // Cascading organizational lists
  const handleBranchChange = async (branchId: string) => {
    setFormData((prev) => ({
      ...prev,
      branchId,
      departmentId: "",
      designationId: "",
    }));
    setDepartments([]);
    setDesignations([]);

    if (branchId) {
      try {
        const filterStr = JSON.stringify({ branchId });
        const res = await fetchHrDepartments(1, 100, "", filterStr);
        if (res.success && res.data?.departments) {
          setDepartments(res.data.departments);
        }
      } catch (err) {
        console.error("Error fetching departments:", err);
      }
    }
  };

  const handleDepartmentChange = async (departmentId: string) => {
    setFormData((prev) => ({
      ...prev,
      departmentId,
      designationId: "",
    }));
    setDesignations([]);

    if (departmentId) {
      try {
        const filterStr = JSON.stringify({ departmentId });
        const res = await fetchHrDesignations(1, 100, "", filterStr);
        if (res.success && res.data?.designations) {
          setDesignations(res.data.designations);
        }
      } catch (err) {
        console.error("Error fetching designations:", err);
      }
    }
  };

  // Cascading location lists
  const handleCountryChange = async (countryId: string) => {
    setFormData((prev) => ({
      ...prev,
      countryId,
      cityId: "",
      districtId: "",
    }));
    setCities([]);
    setDistricts([]);

    if (countryId) {
      try {
        const res = await fetchHrCities(countryId);
        if (res.success && res.data?.cities) {
          setCities(res.data.cities);
        }
      } catch (err) {
        console.error("Error fetching cities:", err);
      }
    }
  };

  const handleCityChange = async (cityId: string) => {
    setFormData((prev) => ({
      ...prev,
      cityId,
      districtId: "",
    }));
    setDistricts([]);

    if (cityId) {
      try {
        const res = await fetchHrDistricts(cityId);
        if (res.success && res.data?.districts) {
          setDistricts(res.data.districts);
        }
      } catch (err) {
        console.error("Error fetching districts:", err);
      }
    }
  };

  // Handle address check mirroring
  const handleAddressCheckboxChange = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      sameAsPermanentAddress: checked,
      permanentAddress: checked ? prev.currentAddress : prev.permanentAddress,
    }));
  };

  const handleCurrentAddressChange = (val: string) => {
    setFormData((prev) => ({
      ...prev,
      currentAddress: val,
      permanentAddress: prev.sameAsPermanentAddress ? val : prev.permanentAddress,
    }));
  };

  // Form Validation and Next/Prev Steps
  const handleNext = () => {
    if (currentStep === 1) {
      if (!formData.firstName) {
        toast.error("First Name is required");
        return;
      }
      if (!formData.personalEmail) {
        toast.error("Personal Email is required");
        return;
      }
      if (!formData.phoneNumber) {
        toast.error("Phone Number is required");
        return;
      }
      if (!formData.gender) {
        toast.error("Gender is required");
        return;
      }
      if (!formData.dateOfBirth) {
        toast.error("Birth Date is required");
        return;
      }
    } else if (currentStep === 2) {
      if (!formData.branchId) {
        toast.error("Branch is required");
        return;
      }
      if (!formData.departmentId) {
        toast.error("Department is required");
        return;
      }
      if (!formData.designationId) {
        toast.error("Designation is required");
        return;
      }
      if (!formData.userRoleId) {
        toast.error("User Role is required");
        return;
      }
      if (!formData.workEmail) {
        toast.error("Work Email is required");
        return;
      }
      if (!formData.joiningDate) {
        toast.error("Joining Date is required");
        return;
      }
      if (!formData.employmentType) {
        toast.error("Employment Type is required");
        return;
      }
      if (!formData.attendanceType) {
        toast.error("Attendance Type is required");
        return;
      }
      if (!formData.salaryCycle) {
        toast.error("Salary Cycle is required");
        return;
      }
    }

    if (currentStep < 3) setCurrentStep(currentStep + 1);
  };

  const handlePrev = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        companyId,
        yearsOfExperience: formData.yearsOfExperience
          ? Number(formData.yearsOfExperience)
          : null,
      };

      const res = await createHrEmployee(payload);
      if (res.success) {
        toast.success("Employee created successfully");
        if (onSuccess) onSuccess();
        onClose();
      } else {
        toast.error(res.message || "Failed to create employee");
      }
    } catch (err: any) {
      console.error(err);
      // err.message comes from hrRequest which extracts the API error body message
      const errMsg = err?.message || "An unexpected error occurred during submission";
      toast.error(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { id: 1, title: "Basic Details" },
    { id: 2, title: "Working Details" },
    { id: 3, title: "Preview" },
  ];

  return (
    <div className="relative rounded-3xl shadow-sm border border-gray-200/50 flex flex-col overflow-hidden bg-white w-full z-10">
      {/* Header */}
      <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 bg-white/50 shrink-0">
        <div>
          <h2 className="text-xl font-semibold text-[#212121]">Add Employee</h2>
          <p className="text-xs text-[#212121]/70">
            Step {currentStep} of 3: {currentStep === 1 ? "Basic Details" : currentStep === 2 ? "Working Details" : "Preview"}
          </p>
        </div>
        <div className="flex items-center gap-3 mt-1">
          {currentStep > 1 && (
            <button
              onClick={handlePrev}
              className="px-4 py-2 border border-slate-200 text-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap cursor-pointer bg-white"
            >
              Back
            </button>
          )}
          <button
            onClick={currentStep === 3 ? handleSubmit : handleNext}
            disabled={isSubmitting}
            className="px-4 py-2 bg-[#FBD037] text-[#212121] text-sm font-semibold rounded-lg hover:bg-[#f5c71a] transition-colors disabled:opacity-50 whitespace-nowrap shadow-sm border-none cursor-pointer"
          >
            {isSubmitting ? "Adding..." : currentStep === 3 ? "Submit" : "Next"}
          </button>
          <button
            onClick={onClose}
            className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors cursor-pointer bg-white text-slate-700 flex items-center justify-center"
          >
            <IconX size={18} />
          </button>
        </div>
      </div>

      <div className="flex flex-col h-full bg-white select-none overflow-y-auto p-6 max-h-[calc(100vh-140px)] space-y-4">
        {/* Step Indicator */}
        <div className="flex items-center justify-center py-2 border-b border-gray-100 shrink-0">
          {steps.map((step) => {
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            return (
              <div key={step.id} className="flex flex-col items-center bg-white px-3">
                <div
                  className={`w-4 h-4 rounded-full flex items-center justify-center font-bold text-[9px] mb-1 transition-colors ${
                    isActive
                      ? "bg-[#FBD037] text-[#212121] ring-2 ring-[#FBD037]/20"
                      : isCompleted
                      ? "bg-[#FBD037] text-[#212121]"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {isCompleted ? <IconCheck size={10} stroke={3} /> : step.id}
                </div>
                <div
                  className={`text-[10px] font-semibold ${
                    isActive || isCompleted ? "text-[#212121]" : "text-gray-400"
                  }`}
                >
                  {step.title}
                </div>
              </div>
            );
          })}
        </div>

        {/* Form Container */}
        <div className="space-y-4 flex-grow">
          {currentStep === 1 && (
            <SectionBlock title="Basic Employee Details" icon={IconUser}>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField
                  label="First Name"
                  placeholder="First name"
                  required
                  value={formData.firstName}
                  onChange={(e) =>
                    setFormData({ ...formData, firstName: e.target.value })
                  }
                />
                <InputField
                  label="Last Name"
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={(e) =>
                    setFormData({ ...formData, lastName: e.target.value })
                  }
                />
                <InputField
                  label="Personal Email"
                  placeholder="personal@example.com"
                  required
                  type="email"
                  value={formData.personalEmail}
                  onChange={(e) =>
                    setFormData({ ...formData, personalEmail: e.target.value })
                  }
                />
                <InputField
                  label="Phone Number"
                  placeholder="Phone number"
                  required
                  value={formData.phoneNumber}
                  onChange={(e) =>
                    setFormData({ ...formData, phoneNumber: e.target.value })
                  }
                />

                <SelectInput
                  label="Gender"
                  required
                  selectOptions={[
                    { value: "MALE", text: "Male" },
                    { value: "FEMALE", text: "Female" },
                    { value: "OTHER", text: "Other" },
                  ]}
                  value={formData.gender}
                  setValue={(val: string) =>
                    setFormData({ ...formData, gender: val })
                  }
                />
                <InputField
                  label="Birth Date"
                  required
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) =>
                    setFormData({ ...formData, dateOfBirth: e.target.value })
                  }
                />
                <SelectInput
                  label="Marital Status"
                  selectOptions={[
                    { value: "SINGLE", text: "Single" },
                    { value: "MARRIED", text: "Married" },
                    { value: "DIVORCED", text: "Divorced" },
                    { value: "WIDOWED", text: "Widowed" },
                  ]}
                  value={formData.maritalStatus}
                  setValue={(val: string) =>
                    setFormData({ ...formData, maritalStatus: val })
                  }
                />
                <SelectInput
                  label="Blood Group"
                  selectOptions={[
                    { value: "A+", text: "A+" },
                    { value: "A-", text: "A-" },
                    { value: "B+", text: "B+" },
                    { value: "B-", text: "B-" },
                    { value: "AB+", text: "AB+" },
                    { value: "AB-", text: "AB-" },
                    { value: "O+", text: "O+" },
                    { value: "O-", text: "O-" },
                  ]}
                  value={formData.bloodGroup}
                  setValue={(val: string) =>
                    setFormData({ ...formData, bloodGroup: val })
                  }
                />

                <InputField
                  label="Nationality"
                  placeholder="Saudi Arabia"
                  value={formData.nationality}
                  onChange={(e) =>
                    setFormData({ ...formData, nationality: e.target.value })
                  }
                />
                <SelectInput
                  label="Country"
                  selectOptions={countries.map((c) => ({
                    value: c.id,
                    text: c.name,
                  }))}
                  value={formData.countryId}
                  setValue={handleCountryChange}
                />
                <SelectInput
                  label="City"
                  disabled={!formData.countryId}
                  selectOptions={cities.map((c) => ({
                    value: c.id,
                    text: c.name,
                  }))}
                  value={formData.cityId}
                  setValue={handleCityChange}
                />
                <SelectInput
                  label="District"
                  disabled={!formData.cityId}
                  selectOptions={districts.map((d) => ({
                    value: d.id,
                    text: d.name,
                  }))}
                  value={formData.districtId}
                  setValue={(val: string) =>
                    setFormData({ ...formData, districtId: val })
                  }
                />

                <InputField
                  label="Zip Code"
                  placeholder="Zip Code"
                  value={formData.zipCode}
                  onChange={(e) =>
                    setFormData({ ...formData, zipCode: e.target.value })
                  }
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-1">
                    Current Address
                  </label>
                  <TextareaField
                    label=""
                    name="currentAddress"
                    placeholder="Enter current address"
                    rows={3}
                    value={formData.currentAddress}
                    onChange={(e) => handleCurrentAddressChange(e.target.value)}
                  />
                </div>

                <div>
                  <div className="flex items-center gap-4 mb-1">
                    <label className="text-sm font-semibold text-gray-700">
                      Permanent Address
                    </label>
                    <label className="relative flex items-center cursor-pointer gap-2 select-none">
                      <input
                        type="checkbox"
                        checked={formData.sameAsPermanentAddress}
                        onChange={(e) =>
                          handleAddressCheckboxChange(e.target.checked)
                        }
                        className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] transition-all bg-white checked:bg-[#FBD037]"
                      />
                      <span className="text-xs font-semibold text-gray-600">
                        Same as Current Address
                      </span>
                    </label>
                  </div>
                  <TextareaField
                    label=""
                    name="permanentAddress"
                    placeholder="Enter permanent address"
                    disabled={formData.sameAsPermanentAddress}
                    rows={3}
                    value={
                      formData.sameAsPermanentAddress
                        ? formData.currentAddress
                        : formData.permanentAddress
                    }
                    onChange={(e) =>
                      setFormData({ ...formData, permanentAddress: e.target.value })
                    }
                  />
                </div>
              </div>
            </SectionBlock>
          )}

          {currentStep === 2 && (
            <SectionBlock title="Working Details" icon={IconBriefcase}>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-4">
                <SelectInput
                  label="Branch"
                  required
                  selectOptions={branches.map((b) => ({
                    value: b.id,
                    text: b.branchName,
                  }))}
                  value={formData.branchId}
                  setValue={handleBranchChange}
                />
                <SelectInput
                  label="Department"
                  required
                  disabled={!formData.branchId}
                  selectOptions={departments.map((d) => ({
                    value: d.id,
                    text: d.departmentName,
                  }))}
                  value={formData.departmentId}
                  setValue={handleDepartmentChange}
                />
                <SelectInput
                  label="Designation"
                  required
                  disabled={!formData.departmentId}
                  selectOptions={designations.map((d) => ({
                    value: d.id,
                    text: d.designationName,
                  }))}
                  value={formData.designationId}
                  setValue={(val: string) =>
                    setFormData({ ...formData, designationId: val })
                  }
                />
                <SelectInput
                  label="User Role"
                  required
                  selectOptions={roles.map((r: any) => ({
                    value: r.id,
                    text: r.roleName,
                  }))}
                  value={formData.userRoleId}
                  setValue={(val: string) =>
                    setFormData({ ...formData, userRoleId: val })
                  }
                />

                <InputField
                  label="Work Email"
                  required
                  placeholder="work@example.com"
                  type="email"
                  value={formData.workEmail}
                  onChange={(e) =>
                    setFormData({ ...formData, workEmail: e.target.value })
                  }
                />
                <InputField
                  label="Joining Date"
                  required
                  type="date"
                  value={formData.joiningDate}
                  onChange={(e) =>
                    setFormData({ ...formData, joiningDate: e.target.value })
                  }
                />
                <SelectInput
                  label="Employment Type"
                  required
                  selectOptions={[
                    { value: "FULL_TIME", text: "Full Time" },
                    { value: "PART_TIME", text: "Part Time" },
                    { value: "INTERN", text: "Intern" },
                    { value: "CONTRACT", text: "Contract" },
                    { value: "FREELANCER", text: "Freelancer" },
                    { value: "TEMPORARY", text: "Temporary" },
                  ]}
                  value={formData.employmentType}
                  setValue={(val: string) =>
                    setFormData({ ...formData, employmentType: val })
                  }
                />
                <SelectInput
                  label="Attendance Type"
                  required
                  selectOptions={[
                    { value: "OFFICE", text: "Office" },
                    { value: "REMOTE", text: "Remote" },
                    { value: "HYBRID", text: "Hybrid" },
                  ]}
                  value={formData.attendanceType}
                  setValue={(val: string) =>
                    setFormData({ ...formData, attendanceType: val })
                  }
                />

                <SelectInput
                  label="Salary Cycle"
                  required
                  selectOptions={[
                    { value: "WEEKLY", text: "Weekly" },
                    { value: "BI_WEEKLY", text: "Bi-Weekly" },
                    { value: "MONTHLY", text: "Monthly" },
                    { value: "YEARLY", text: "Yearly" },
                  ]}
                  value={formData.salaryCycle}
                  setValue={(val: string) =>
                    setFormData({ ...formData, salaryCycle: val })
                  }
                />
                <InputField
                  label="Annual CTC"
                  placeholder="Ex. 120000"
                  type="number"
                  value={formData.annualCtc}
                  onChange={(e) =>
                    setFormData({ ...formData, annualCtc: e.target.value })
                  }
                />
                <InputField
                  label="Years of Experience"
                  placeholder="Ex. 5"
                  type="number"
                  value={formData.yearsOfExperience}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      yearsOfExperience: e.target.value,
                    })
                  }
                />
                <SelectInput
                  label="Reporting To"
                  selectOptions={managers.map((m) => ({
                    value: m.id,
                    text: `${m.firstName} ${m.lastName || ""}`,
                  }))}
                  value={formData.reportingToId}
                  setValue={(val: string) =>
                    setFormData({ ...formData, reportingToId: val })
                  }
                />

                <InputField
                  label="Current Work"
                  placeholder="Ex. Manager"
                  value={formData.currentWork}
                  onChange={(e) =>
                    setFormData({ ...formData, currentWork: e.target.value })
                  }
                />
                <InputField
                  label="Past Work"
                  placeholder="Ex. Supervisor"
                  value={formData.pastWork}
                  onChange={(e) =>
                    setFormData({ ...formData, pastWork: e.target.value })
                  }
                />
                <InputField
                  label="Previous Company"
                  placeholder="Ex. ABC Co."
                  value={formData.previousCompany}
                  onChange={(e) =>
                    setFormData({ ...formData, previousCompany: e.target.value })
                  }
                />
                <InputField
                  label="Iqama Number"
                  placeholder="Saudi Residency Permit ID"
                  value={formData.iqamaNumber}
                  onChange={(e) =>
                    setFormData({ ...formData, iqamaNumber: e.target.value })
                  }
                />

                <InputField
                  label="Iqama Document Link"
                  placeholder="Enter document path or link"
                  value={formData.iqamaDoc}
                  onChange={(e) =>
                    setFormData({ ...formData, iqamaDoc: e.target.value })
                  }
                />
              </div>
            </SectionBlock>
          )}

          {currentStep === 3 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300 min-h-[400px] flex flex-col items-center justify-center text-center">
              <IconEye size={48} className="text-gray-300 mb-2 animate-bounce" />
              <h3 className="text-xl font-bold text-gray-700">
                Ready to Create Employee
              </h3>
              <p className="text-gray-500 max-w-md">
                Please double-check the information for{" "}
                <strong>
                  {formData.firstName} {formData.lastName}
                </strong>{" "}
                before finalizing the profile.
              </p>
              <div className="bg-gray-50 p-4 rounded-xl text-left text-xs text-gray-600 max-w-lg w-full max-h-48 overflow-y-auto space-y-1">
                <div>
                  <strong>Name:</strong> {formData.firstName} {formData.lastName}
                </div>
                <div>
                  <strong>Work Email:</strong> {formData.workEmail}
                </div>
                <div>
                  <strong>Phone:</strong> {formData.phoneNumber}
                </div>
                <div>
                  <strong>Iqama Number:</strong> {formData.iqamaNumber || "N/A"}
                </div>
                <div>
                  <strong>Branch:</strong>{" "}
                  {branches.find((b) => b.id === formData.branchId)?.branchName ||
                    formData.branchId}
                </div>
                <div>
                  <strong>Department:</strong>{" "}
                  {departments.find((d) => d.id === formData.departmentId)
                    ?.departmentName || formData.departmentId}
                </div>
                <div>
                  <strong>Designation:</strong>{" "}
                  {designations.find((d) => d.id === formData.designationId)
                    ?.designationName || formData.designationId}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
